import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Creator {
  id: number;
  name: string;
  avatar: string;
  cover: string;
  category: string;
  subscribers: number;
  minPrice: number;
  isSubscribed: boolean;
  isSaved?: boolean;
  subscribedLevel?: number;
}

interface Comment {
  id: number;
  user: string;
  text: string;
  time: string;
}

export default function FanDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'home' | 'subscribed' | 'saved' | 'messages'>('home');
  const [showComments, setShowComments] = useState<number | null>(null);
  const [commentText, setCommentText] = useState('');
  const [savedCreators, setSavedCreators] = useState<number[]>([]);
  const [showTipModal, setShowTipModal] = useState<number | null>(null);
  const [tipAmount, setTipAmount] = useState('');
  const [tipMessage, setTipMessage] = useState('');
  const [balance, setBalance] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [subscribedCreatorIds, setSubscribedCreatorIds] = useState<number[]>([]);
  const [comments, setComments] = useState<Record<number, Comment[]>>({
    1: [
      { id: 1, user: '유저123', text: '너무 예뻐요! 💕', time: '1시간 전' },
      { id: 2, user: '팬456', text: '오늘도 좋은 콘텐츠 감사합니다!', time: '30분 전' }
    ],
    2: [
      { id: 1, user: '구독자789', text: '화보 최고예요!', time: '2시간 전' }
    ]
  });

  // 사용자 인증 및 데이터 로드
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const userData = JSON.parse(localStorage.getItem('currentUser') || '{}');
      setCurrentUser(userData);

      // 사용자별 데이터 키
      const userKey = `user_${user.id}`;

      // 잔액 로드 (첫 로그인 시 0)
      const userBalance = localStorage.getItem(`${userKey}_balance`);
      setBalance(userBalance ? Number(userBalance) : 0);

      // 구독 목록 로드 (첫 로그인 시 빈 배열)
      const userSubscriptions = localStorage.getItem(`${userKey}_subscriptions`);
      setSubscribedCreatorIds(userSubscriptions ? JSON.parse(userSubscriptions) : []);

      // 관심 목록 로드 (첫 로그인 시 빈 배열)
      const userSaved = localStorage.getItem(`${userKey}_saved`);
      setSavedCreators(userSaved ? JSON.parse(userSaved) : []);
    };

    checkAuth();
  }, [navigate]);

  const quickTipAmounts = [1, 3, 5, 10, 30, 100];

  const recommendedCreators: Creator[] = [
    {
      id: 1,
      name: '민지',
      avatar: 'https://public.readdy.ai/ai/img_res/f548c036cf6a549d0fd84896797a261c.jpg',
      cover: 'https://readdy.ai/api/search-image?query=soft%20pastel%20pink%20purple%20gradient%20abstract%20background%20with%20sparkles%20dreamy%20aesthetic%20smooth%20texture%20elegant%20minimalist%20design&width=800&height=600&seq=fan1cover&orientation=landscape',
      category: '일상/브이로그',
      subscribers: 1250,
      minPrice: 10,
      isSubscribed: false,
      isSaved: false
    },
    {
      id: 2,
      name: '하늘',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20artist%20content%20creator%20profile%20picture%20with%20purple%20lighting%20creative%20aesthetic%20professional%20photo%20clean%20background&width=200&height=200&seq=fan2&orientation=squarish',
      cover: 'https://readdy.ai/api/search-image?query=dreamy%20purple%20blue%20gradient%20background%20with%20soft%20glow%20aesthetic%20pastel%20colors%20smooth%20abstract%20design%20elegant%20minimalist&width=800&height=600&seq=fan2cover&orientation=landscape',
      category: '아트/일러스트',
      subscribers: 890,
      minPrice: 20,
      isSubscribed: true,
      subscribedLevel: 2,
      isSaved: false
    },
    {
      id: 3,
      name: '서연',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20fitness%20creator%20profile%20photo%20with%20bright%20lighting%20healthy%20athletic%20look%20professional%20headshot%20clean%20background&width=200&height=200&seq=fan3&orientation=squarish',
      cover: 'https://readdy.ai/api/search-image?query=soft%20pink%20coral%20gradient%20background%20with%20gentle%20sparkles%20fitness%20aesthetic%20smooth%20pastel%20colors%20elegant%20minimalist%20design&width=800&height=600&seq=fan3cover&orientation=landscape',
      category: '운동/헬스',
      subscribers: 2100,
      minPrice: 15,
      isSubscribed: false,
      isSaved: false
    },
    {
      id: 4,
      name: '지우',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20gaming%20streamer%20profile%20picture%20with%20neon%20lighting%20playful%20expression%20professional%20photo%20clean%20background&width=200&height=200&seq=fan4&orientation=squarish',
      cover: 'https://readdy.ai/api/search-image?query=vibrant%20purple%20pink%20gradient%20background%20with%20digital%20glow%20gaming%20aesthetic%20smooth%20pastel%20colors%20modern%20minimalist%20design&width=800&height=600&seq=fan4cover&orientation=landscape',
      category: '게임/스트리밍',
      subscribers: 3400,
      minPrice: 9,
      isSubscribed: true,
      subscribedLevel: 1,
      isSaved: false
    },
    {
      id: 5,
      name: '유나',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20beauty%20creator%20profile%20photo%20with%20soft%20lighting%20makeup%20artist%20professional%20headshot%20elegant%20clean%20background&width=200&height=200&seq=fan5&orientation=squarish',
      cover: 'https://readdy.ai/api/search-image?query=elegant%20pink%20rose%20gradient%20background%20with%20soft%20sparkles%20beauty%20aesthetic%20smooth%20pastel%20colors%20luxurious%20minimalist%20design&width=800&height=600&seq=fan5cover&orientation=landscape',
      category: '뷰티/메이크업',
      subscribers: 1800,
      minPrice: 11,
      isSubscribed: false,
      isSaved: false
    },
    {
      id: 6,
      name: '채원',
      avatar: 'https://readdy.ai/api/search-image?query=korean%20female%20music%20creator%20profile%20picture%20with%20purple%20lighting%20artistic%20expression%20professional%20photo%20clean%20background&width=200&height=200&seq=fan6&orientation=squarish',
      cover: 'https://readdy.ai/api/search-image?query=dreamy%20lavender%20purple%20gradient%20background%20with%20musical%20notes%20aesthetic%20smooth%20pastel%20colors%20artistic%20minimalist%20design&width=800&height=600&seq=fan6cover&orientation=landscape',
      category: '음악/커버',
      subscribers: 950,
      minPrice: 13,
      isSubscribed: false,
      isSaved: false
    }
  ];

  // 구독 중인 크리에이터 필터링 (사용자별 데이터 사용)
  const subscribedCreators = recommendedCreators.filter(c => subscribedCreatorIds.includes(c.id));

  const savedCreatorsList = recommendedCreators.filter(c => savedCreators.includes(c.id));

  const displayCreators = 
    activeTab === 'subscribed' ? subscribedCreators : 
    activeTab === 'saved' ? savedCreatorsList :
    recommendedCreators;

  const handleSave = (creatorId: number, e: React.MouseEvent) => {
    e.preventDefault();
    const newSaved = savedCreators.includes(creatorId) 
      ? savedCreators.filter(id => id !== creatorId)
      : [...savedCreators, creatorId];
    
    setSavedCreators(newSaved);
    
    // 사용자별 저장
    if (currentUser?.id) {
      const userKey = `user_${currentUser.id}`;
      localStorage.setItem(`${userKey}_saved`, JSON.stringify(newSaved));
    }
  };

  const handleAddComment = (creatorId: number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    
    const newComment: Comment = {
      id: Date.now(),
      user: '나',
      text: commentText,
      time: '방금 전'
    };
    
    setComments(prev => ({
      ...prev,
      [creatorId]: [...(prev[creatorId] || []), newComment]
    }));
    setCommentText('');
  };

  const toggleComments = (creatorId: number, e: React.MouseEvent) => {
    e.preventDefault();
    setShowComments(showComments === creatorId ? null : creatorId);
  };

  const handleSendTip = () => {
    if (!tipAmount || Number(tipAmount) < 1) {
      alert('최소 후원 금액은 1피치입니다.');
      return;
    }
    if (Number(tipAmount) > balance) {
      alert('잔액이 부족합니다. 충전 후 이용해주세요.');
      return;
    }
    
    // 잔액 차감
    const newBalance = balance - Number(tipAmount);
    setBalance(newBalance);
    
    // 사용자별 저장
    if (currentUser?.id) {
      const userKey = `user_${currentUser.id}`;
      localStorage.setItem(`${userKey}_balance`, newBalance.toString());
    }
    
    alert(`${Number(tipAmount).toLocaleString()}피치 후원이 완료되었습니다! 💝`);
    setShowTipModal(null);
    setTipAmount('');
    setTipMessage('');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('currentUser');
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-white relative">
      {/* Subtle Background Pattern - 매우 은은한 배경 */}
      <div className="fixed inset-0 pointer-events-none">
        {/* 은은한 그라데이션 */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50/20 via-purple-50/10 to-blue-50/20"></div>
        
        {/* 미세한 도트 패턴 */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(236, 72, 153, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        
        {/* 부드러운 빛 효과 */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl"></div>
      </div>

      {/* Top Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <div className="text-2xl font-black bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                MyFanHub
              </div>
            </Link>

            {/* Right Side */}
            <div className="flex items-center gap-4">
              <Link
                to="/fan/wallet"
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                <span className="text-xl">🍑</span>
                <span className="font-semibold text-gray-800 text-sm">{balance}개</span>
              </Link>
              <div className="relative group">
                <button className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold hover:scale-110 transition-transform">
                  {currentUser?.username?.[0]?.toUpperCase() || '팬'}
                </button>
                <div className="absolute right-0 top-12 w-48 bg-white rounded-xl shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <div className="p-3 border-b border-gray-100">
                    <p className="font-semibold text-gray-800">{currentUser?.username || '사용자'}</p>
                    <p className="text-xs text-gray-500">{currentUser?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-b-xl transition-colors"
                  >
                    로그아웃
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Tab Navigation */}
      <div className="sticky top-16 z-40 bg-white/90 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center gap-8">
            <button
              onClick={() => setActiveTab('home')}
              className={`py-3 px-1 font-semibold transition-all whitespace-nowrap relative ${
                activeTab === 'home'
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              추천
              {activeTab === 'home' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('subscribed')}
              className={`py-3 px-1 font-semibold transition-all whitespace-nowrap relative ${
                activeTab === 'subscribed'
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              구독 중
              {activeTab === 'subscribed' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></div>
              )}
            </button>
            <Link
              to="/fan/explore"
              className="py-3 px-1 font-semibold text-gray-400 hover:text-gray-600 transition-all whitespace-nowrap"
            >
              탐색
            </Link>
            <button
              onClick={() => setActiveTab('saved')}
              className={`py-3 px-1 font-semibold transition-all whitespace-nowrap relative ${
                activeTab === 'saved'
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              관심
              {activeTab === 'saved' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-3 px-1 font-semibold transition-all whitespace-nowrap relative ${
                activeTab === 'messages'
                  ? 'text-gray-900'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              메시지
              {activeTab === 'messages' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900"></div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto relative">
        {/* Messages Tab Content */}
        {activeTab === 'messages' && (
          <div className="text-center py-20 px-4">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-message-3-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">메시지가 없습니다</h3>
            <p className="text-gray-600 mb-6">크리에이터와 대화를 시작해보세요!</p>
          </div>
        )}

        {/* Creators Feed */}
        {activeTab !== 'messages' && (
          <>
            {displayCreators.length === 0 ? (
              <div className="text-center py-20 px-4">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <i className={`${activeTab === 'saved' ? 'ri-heart-line' : 'ri-heart-line'} text-4xl text-gray-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {activeTab === 'saved' ? '관심 콘텐츠가 없습니다' : '아직 구독 중인 크리에이터가 없습니다'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {activeTab === 'saved' ? '마음에 드는 콘텐츠를 저장해보세요!' : '마음에 드는 크리에이터를 찾아 구독해보세요!'}
                </p>
                <button
                  onClick={() => setActiveTab('home')}
                  className="px-6 py-3 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-all whitespace-nowrap"
                >
                  크리에이터 둘러보기
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {displayCreators.map((creator) => (
                  <div key={creator.id} className="block group bg-white/50 backdrop-blur-sm">
                    {/* Creator Header */}
                    <div className="flex items-center gap-3 px-4 py-3">
                      <Link to={`/fan/creator/${creator.id}`} className="flex items-center gap-3 flex-1">
                        <div className="w-10 h-10 rounded-full overflow-hidden border border-gray-200">
                          <img
                            src={creator.avatar}
                            alt={creator.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{creator.name}</h3>
                            {creator.isSubscribed && (
                              <i className="ri-heart-3-fill text-pink-500 text-sm"></i>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{creator.category}</p>
                        </div>
                      </Link>
                      <button className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
                        <i className="ri-more-2-fill text-gray-600"></i>
                      </button>
                    </div>

                    {/* Cover Image */}
                    <Link to={`/fan/creator/${creator.id}`} className="block">
                      <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
                        <img
                          src={creator.cover}
                          alt={creator.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Actions & Info */}
                    <div className="px-4 py-3">
                      {/* Action Buttons */}
                      <div className="flex items-center gap-4 mb-3">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            alert('좋아요!');
                          }}
                          className="hover:text-gray-500 transition-colors"
                        >
                          <i className="ri-heart-line text-2xl"></i>
                        </button>
                        <button
                          onClick={(e) => toggleComments(creator.id, e)}
                          className="hover:text-gray-500 transition-colors"
                        >
                          <i className="ri-chat-3-line text-2xl"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTipModal(creator.id);
                          }}
                          className="hover:text-gray-500 transition-colors"
                        >
                          <i className="ri-gift-line text-2xl"></i>
                        </button>
                        <button
                          onClick={(e) => handleSave(creator.id, e)}
                          className={`ml-auto hover:text-gray-500 transition-colors ${
                            savedCreators.includes(creator.id) ? 'text-gray-900' : ''
                          }`}
                        >
                          <i className={`${savedCreators.includes(creator.id) ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-2xl`}></i>
                        </button>
                      </div>

                      {/* Stats */}
                      <div className="mb-2">
                        <p className="font-semibold text-sm text-gray-900">
                          구독자 {creator.subscribers.toLocaleString()}명
                        </p>
                        <p className="text-sm text-gray-600">
                          최소 가격: {creator.minPrice.toLocaleString()}피치/월
                        </p>
                      </div>

                      {/* Comments Section */}
                      {showComments === creator.id && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          {/* Existing Comments */}
                          <div className="space-y-3 mb-4">
                            {(comments[creator.id] || []).map((comment) => (
                              <div key={comment.id} className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                  {comment.user[0]}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-sm text-gray-900">{comment.user}</span>
                                    <span className="text-xs text-gray-500">{comment.time}</span>
                                  </div>
                                  <p className="text-sm text-gray-800">{comment.text}</p>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Add Comment */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={commentText}
                              onChange={(e) => setCommentText(e.target.value)}
                              placeholder="댓글을 입력하세요..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-pink-500 text-sm"
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleAddComment(creator.id, e as any);
                                }
                              }}
                            />
                            <button
                              onClick={(e) => handleAddComment(creator.id, e)}
                              className="px-4 py-2 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-all whitespace-nowrap text-sm"
                            >
                              게시
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Subscribe Info */}
                      {!creator.isSubscribed && (
                        <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg mt-3">
                          <span className="text-sm text-gray-700">
                            {creator.minPrice.toLocaleString()}피치부터 구독 가능
                          </span>
                          <Link
                            to={`/fan/creator/${creator.id}`}
                            className="px-4 py-1.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all whitespace-nowrap"
                          >
                            구독하기
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Tip Modal */}
      {showTipModal !== null && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-md w-full">
            <div className="border-b border-gray-100 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">후원하기</h2>
              <button
                onClick={() => {
                  setShowTipModal(null);
                  setTipAmount('');
                  setTipMessage('');
                }}
                className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <i className="ri-close-line text-2xl text-gray-600"></i>
              </button>
            </div>

            <div className="p-6">
              {/* Creator Info */}
              <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200">
                  <img
                    src={recommendedCreators.find(c => c.id === showTipModal)?.avatar}
                    alt="Creator"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {recommendedCreators.find(c => c.id === showTipModal)?.name}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {recommendedCreators.find(c => c.id === showTipModal)?.category}
                  </p>
                </div>
              </div>

              {/* Balance Info */}
              <div className="mb-6 p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">보유 피치</span>
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-bold text-gray-900">{balance.toLocaleString()}</span>
                    <span className="text-lg">🍑</span>
                  </div>
                </div>
              </div>

              {/* Quick Tip Amounts */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">빠른 선택</p>
                <div className="grid grid-cols-3 gap-3">
                  {quickTipAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setTipAmount(amount.toString())}
                      className={`py-3 rounded-xl font-semibold transition-all whitespace-nowrap ${
                        tipAmount === amount.toString()
                          ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {amount}🍑
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">직접 입력</p>
                <div className="relative">
                  <input
                    type="number"
                    value={tipAmount}
                    onChange={(e) => setTipAmount(e.target.value)}
                    placeholder="후원 피치 입력"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none text-lg"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">🍑</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">최소 후원: 1피치</p>
              </div>

              {/* Tip Message */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">응원 메시지 (선택)</p>
                <textarea
                  value={tipMessage}
                  onChange={(e) => setTipMessage(e.target.value)}
                  placeholder="크리에이터에게 전할 메시지를 입력하세요..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-pink-500 focus:outline-none resize-none"
                  rows={3}
                  maxLength={200}
                />
                <p className="text-xs text-gray-500 mt-2">{tipMessage.length}/200</p>
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendTip}
                disabled={!tipAmount || Number(tipAmount) < 1}
                className="w-full py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {tipAmount ? `${Number(tipAmount).toLocaleString()}피치 후원하기` : '금액을 입력하세요'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation (Mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-gray-200/50 z-50">
        <div className="flex items-center justify-around py-2">
          <button
            onClick={() => setActiveTab('home')}
            className={`flex flex-col items-center gap-1 py-1 ${
              activeTab === 'home' ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            <i className="ri-home-4-line text-2xl"></i>
            <span className="text-xs font-medium">홈</span>
          </button>
          <Link
            to="/fan/explore"
            className="flex flex-col items-center gap-1 py-1 text-gray-400"
          >
            <i className="ri-search-line text-2xl"></i>
            <span className="text-xs font-medium">탐색</span>
          </Link>
          <button
            onClick={() => setActiveTab('subscribed')}
            className={`flex flex-col items-center gap-1 py-1 ${
              activeTab === 'subscribed' ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            <i className="ri-heart-3-line text-2xl"></i>
            <span className="text-xs font-medium">구독</span>
          </button>
          <button
            onClick={() => setActiveTab('saved')}
            className={`flex flex-col items-center gap-1 py-1 ${
              activeTab === 'saved' ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            <i className="ri-heart-line text-2xl"></i>
            <span className="text-xs font-medium">관심</span>
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`flex flex-col items-center gap-1 py-1 ${
              activeTab === 'messages' ? 'text-gray-900' : 'text-gray-400'
            }`}
          >
            <i className="ri-message-3-line text-2xl"></i>
            <span className="text-xs font-medium">메시지</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
